import { useMutation, UseMutationOptions, useQueryClient, type QueryKey } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    PurchaseOrderEntry,
    PurchaseOrderPart,
    PurchaseOrdersFeedResponse,
    SubmitPurchaseOrderInput,
    SubmitPurchaseOrderPart,
    SubmitPurchaseOrderResponse,
} from '../../types/inventory/purchaseOrders';

const FEED_QUERY_ROOT = '/inventory/purchase-orders/feed';
/** Matches `useGetPurchaseOrdersFeed` queryKey index for statusFilter. */
const FEED_STATUS_FILTER_INDEX = 2;

type FeedSnapshot = Array<[QueryKey, PurchaseOrdersFeedResponse | undefined]>;

const submitPurchaseOrder = async (
    sessionId: string,
    input: SubmitPurchaseOrderInput,
): Promise<SubmitPurchaseOrderResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/purchase-orders/submit`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

function partsForStatusFilter(
    parts: PurchaseOrderPart[],
    statusFilter: string,
): PurchaseOrderPart[] {
    if (statusFilter === 'pending') {
        return parts.filter((part) => part.status === 'pending');
    }
    if (statusFilter === 'completed') {
        return parts.filter(
            (part) => part.status === 'confirmed' || part.status === 'rejected',
        );
    }
    return parts;
}

function applyPartAction(
    part: PurchaseOrderPart,
    action: SubmitPurchaseOrderPart,
): PurchaseOrderPart {
    if (action.action === 'confirm') {
        return {
            ...part,
            status: 'confirmed',
            deliveryDays: action.deliveryDays,
        };
    }
    return { ...part, status: 'rejected' };
}

function applyOptimisticSubmit(
    data: PurchaseOrdersFeedResponse,
    input: SubmitPurchaseOrderInput,
    statusFilter: string,
): PurchaseOrdersFeedResponse {
    const actionsByPartId = new Map(
        input.parts.map((part) => [part.partId, part] as const),
    );
    let removedRows = 0;

    const requests = data.requests.flatMap((request): PurchaseOrderEntry[] => {
        if (request.purchaseOrderId !== input.purchaseOrderId) return [request];

        const nextParts = request.parts.map((part) => {
            const action = actionsByPartId.get(part.partId);
            return action ? applyPartAction(part, action) : part;
        });
        const hasPending = nextParts.some((part) => part.status === 'pending');
        const filteredParts = partsForStatusFilter(nextParts, statusFilter);
        if (filteredParts.length === 0) {
            removedRows += 1;
            return [];
        }
        return [
            {
                ...request,
                status: hasPending ? 'pending' : 'completed',
                parts: filteredParts,
            },
        ];
    });

    return {
        ...data,
        requests,
        total: Math.max(0, data.total - removedRows),
    };
}

export const useSubmitPurchaseOrder = (
    options?: UseMutationOptions<SubmitPurchaseOrderResponse, unknown, SubmitPurchaseOrderInput>,
) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: SubmitPurchaseOrderInput) =>
            submitPurchaseOrder(sessionContext?.sessionId ?? '', input),
        ...options,
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey: [FEED_QUERY_ROOT] });

            const previous = queryClient.getQueriesData<PurchaseOrdersFeedResponse>({
                queryKey: [FEED_QUERY_ROOT],
            }) as FeedSnapshot;

            for (const [queryKey, data] of previous) {
                if (!data) continue;
                const statusFilter = String(queryKey[FEED_STATUS_FILTER_INDEX] ?? 'pending');
                queryClient.setQueryData<PurchaseOrdersFeedResponse>(
                    queryKey,
                    applyOptimisticSubmit(data, input, statusFilter),
                );
            }

            return { previous };
        },
        onError: (error, input, onMutateResult, context) => {
            const previous = onMutateResult?.previous as FeedSnapshot | undefined;
            if (previous) {
                for (const [queryKey, data] of previous) {
                    queryClient.setQueryData(queryKey, data);
                }
            }
            options?.onError?.(error, input, onMutateResult, context);
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [FEED_QUERY_ROOT] });
            queryClient.invalidateQueries({ queryKey: ['/order/detail'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            options?.onSuccess?.(...args);
        },
    });
};
